import apiClient from './client';

export interface SystemConfig {
  environment: {
    app_name: string;
    environment: string;
    debug: boolean;
    log_level: string;
  };
  database: {
    host: string;
    port: number;
    database: string;
    pool_size: number;
    max_overflow: number;
  };
  langfuse: {
    host: string;
    public_key: string;
    enabled: boolean;
  };
  services: {
    agent_server_url: string;
  };
  security: {
    cors_origins: string[];
    access_token_expire_minutes: number;
    refresh_token_expire_days: number;
  };
  agent_api_key: string | null;
}

export interface ApiKeyStatus {
  langfuse: {
    public_key: string;
    secret_key: string;
    configured: boolean;
  };
  jwt: {
    secret_key: string;
    algorithm: string;
    configured: boolean;
  };
}

export const settingsApi = {
  async getSystemConfig(): Promise<SystemConfig> {
    const response = await apiClient.get<SystemConfig>('/settings/system');
    return response.data;
  },

  async getApiKeyStatus(): Promise<ApiKeyStatus> {
    const response = await apiClient.get<ApiKeyStatus>('/settings/api-keys');
    return response.data;
  },

  async getPromptLabels(): Promise<string[]> {
    const response = await apiClient.get<{ labels: string[] }>('/settings/prompt-labels');
    return response.data.labels;
  },
};

// Cached agent API key for direct agent-server calls
let _cachedAgentApiKey: string | null = null;
let _cacheLoadedAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get agent API key (cached, fetched from admin-server settings).
 * Used by agent.ts, users.ts, threads.ts for X-API-Key header.
 */
export async function getAgentApiKey(): Promise<string | null> {
  const now = Date.now();
  if (_cachedAgentApiKey && now - _cacheLoadedAt < CACHE_TTL_MS) {
    return _cachedAgentApiKey;
  }
  try {
    const config = await settingsApi.getSystemConfig();
    _cachedAgentApiKey = config.agent_api_key || null;
    _cacheLoadedAt = now;
    return _cachedAgentApiKey;
  } catch {
    return _cachedAgentApiKey;
  }
}

/**
 * Get headers object including X-API-Key for direct agent-server calls.
 */
export async function getAgentHeaders(extra: Record<string, string> = {}): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...extra };
  const apiKey = await getAgentApiKey();
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }
  return headers;
}
